import React, { useCallback, useEffect, useMemo, useState } from "react";
import Header from "@/components/Header";
import { toast, Toaster } from "sonner";
import { HelpCircle, BookOpen, Trash2 } from "lucide-react";

import PayoffChart from "@/components/forge/PayoffChart";
import GreeksTable from "@/components/forge/GreeksTable";
import StressTestPanel from "@/components/forge/StressTestPanel";
import MarginForecastPanel from "@/components/forge/MarginForecastPanel";
import StrategyLog from "@/components/forge/StrategyLog";
import AlphaInsights from "@/components/forge/AlphaInsights";
import OptimizerModal from "@/components/forge/OptimizerModal";
import SavedStrategiesDrawer from "@/components/forge/SavedStrategiesDrawer";
import RecommendModal from "@/components/forge/RecommendModal";
import ShareModal from "@/components/forge/ShareModal";
import ForgeModal from "@/components/forge/ForgeModal";
import ForgeHeaderBar from "@/components/forge/ForgeHeaderBar";
import StrategyPickerModal from "@/components/forge/StrategyPickerModal";
import MetricsStrip from "@/components/forge/MetricsStrip";
import StrikeRibbon from "@/components/forge/StrikeRibbon";
import ViewTabs from "@/components/forge/ViewTabs";
import ExpirationStrip from "@/components/forge/ExpirationStrip";
import OptionChainTable from "@/components/forge/OptionChainTable";
import PayoffTable from "@/components/forge/PayoffTable";
import ContractValueTable from "@/components/forge/ContractValueTable";
import ChartScenarioBar from "@/components/forge/ChartScenarioBar";
import MatrixHeatmap from "@/components/forge/MatrixHeatmap";
import StrategyOfDayCard from "@/components/forge/StrategyOfDayCard";
import { classifyStrategy } from "@/components/forge/autoStrategyName";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

import {
  fetchForgeTemplates, fetchForgeChain, buildForgeStrategy,
} from "@/lib/forgeApi";
import {
  fetchForgeTickers,
  resolveForgeTicker,
  buildCustomStrategy,
} from "@/lib/academyApi";
import { fetchAnalysis } from "@/lib/api";

const LS_TICKER = "forge_ticker_v2";
const DEFAULT_IV = 0.20;

export default function ForgePage() {
  const [templates, setTemplates] = useState([]);
  const [pickerData, setPickerData] = useState({ indices: [], futures: [] });
  const [ticker, setTicker] = useState(() => {
    try { return localStorage.getItem(LS_TICKER) || "SPY"; } catch { return "SPY"; }
  });

  // Live chain + price
  const [chain, setChain] = useState(null);
  const [chainLoading, setChainLoading] = useState(false);
  const [spot, setSpot] = useState(null);
  const [changePct, setChangePct] = useState(null);
  const [iv30, setIv30] = useState(DEFAULT_IV);

  // Active expiration
  const [activeExpiry, setActiveExpiry] = useState(null);

  // Strategy legs (the user's working position) — primary state in the
  // strike-grid flow. Every Bid/Ask click pushes onto this array.
  const [legs, setLegs] = useState([]);
  const [strategy, setStrategy] = useState(null);
  const [buildLoading, setBuildLoading] = useState(false);

  // Modals
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const [showRecommend, setShowRecommend] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [showStressMargin, setShowStressMargin] = useState(false);
  const [showLogAlpha, setShowLogAlpha] = useState(false);
  const [viewMode, setViewMode] = useState("graph");

  // Scenario state — lifted from PayoffChart so the new horizontal footer
  // bar can drive it in tandem with the chart's overlays.
  const [scnTimeFrac, setScnTimeFrac]   = useState(0);
  const [scnShockPct, setScnShockPct]   = useState(0);
  const [scnIvShockVp, setScnIvShockVp] = useState(0);

  // Fetch templates + ticker picker once
  useEffect(() => {
    fetchForgeTemplates().then((d) => setTemplates(d.templates || [])).catch(() => {});
    fetchForgeTickers()
      .then((d) => setPickerData(d || { indices: [], futures: [] }))
      .catch(() => setPickerData({ indices: [], futures: [] }));
  }, []);

  // Resolve spot + IV + Δ% when ticker changes
  useEffect(() => {
    if (!ticker) return;
    try { localStorage.setItem(LS_TICKER, ticker); } catch { /* ignore */ }
    let alive = true;
    const isIdx =
      ticker.startsWith("/") ||
      ["SPX", "XSP", "NDX", "RUT", "VIX", "DJX"].includes(ticker);
    const fetcher = isIdx
      ? resolveForgeTicker(ticker).then(async (info) => {
          const a = await fetchAnalysis(info.proxy).catch(() => null);
          return {
            spot: a?.quote?.price,
            iv30: a?.options?.iv30 || info.default_iv,
            changePct: a?.quote?.changePercent ?? a?.quote?.change_pct,
          };
        })
      : fetchAnalysis(ticker).then((d) => ({
          spot: d?.quote?.price,
          iv30: d?.options?.iv30,
          changePct: d?.quote?.changePercent ?? d?.quote?.change_pct,
        }));
    fetcher
      .then((d) => {
        if (!alive) return;
        if (d?.spot) setSpot(d.spot);
        if (d?.iv30) setIv30(d.iv30);
        if (Number.isFinite(d?.changePct)) setChangePct(d.changePct);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [ticker]);

  // Fetch the option chain whenever the ticker changes — also resets legs
  useEffect(() => {
    if (!ticker) return;
    let alive = true;
    setChainLoading(true);
    setLegs([]);            // wipe legs when switching tickers
    setStrategy(null);
    fetchForgeChain(ticker)
      .then((c) => {
        if (!alive) return;
        setChain(c || null);
        const exps = c?.expirations || [];
        // Pick the first expiry ~30d out, else the nearest non-zero, else the first
        const tgt = exps.find((e) => e.dte >= 25 && e.dte <= 45)
          || exps.find((e) => e.dte > 0)
          || exps[0];
        setActiveExpiry(tgt?.date || null);
        if (c?.spot) setSpot(c.spot);

        // Pre-load a friendly starter strategy on first visit: ATM long call.
        // Pick the nearest-strike call from the ~30d expiry. Skipped if the
        // user already has legs saved or if chain data is incomplete.
        try {
          const seeded = localStorage.getItem("forge_starter_seeded");
          const callsBucket = c?.quote_table?.[tgt?.date]?.calls;
          if (!seeded && tgt && c?.spot && callsBucket) {
            const strikeKeys = Object.keys(callsBucket).map((k) => parseFloat(k))
              .filter((n) => Number.isFinite(n));
            if (strikeKeys.length) {
              const atm = strikeKeys.reduce(
                (best, s) => Math.abs(s - c.spot) < Math.abs(best - c.spot) ? s : best,
                strikeKeys[0],
              );
              setLegs([{ action: "BUY", type: "call", strike: atm, dte: tgt.dte, qty: 1 }]);
              localStorage.setItem("forge_starter_seeded", "1");
            }
          }
        } catch { /* localStorage unavailable — skip */ }
      })
      .catch(() => alive && setChain(null))
      .finally(() => alive && setChainLoading(false));
    return () => { alive = false; };
  }, [ticker]);

  // Rebuild strategy whenever legs change (debounced)
  useEffect(() => {
    if (!legs.length || !spot) {
      setStrategy(null);
      return undefined;
    }
    const t = setTimeout(async () => {
      setBuildLoading(true);
      try {
        const res = await buildCustomStrategy({
          legs,
          spot,
          iv: iv30,
          r: 0.045,
          symbol: ticker,
        });
        setStrategy(res);
      } catch (e) {
        console.error("build failed", e);
        toast.error(e?.response?.data?.detail || "Strategy build failed");
        setStrategy(null);
      } finally {
        setBuildLoading(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [legs, spot, iv30, ticker]);

  // ── Chain interactions ─────────────────────────────────────────
  const addLegFromChain = useCallback(({ action, type, strike, dte }) => {
    setLegs((prev) => {
      // De-dupe: if the same action/type/strike/dte already exists, bump qty.
      const idx = prev.findIndex(
        (l) =>
          l.action === action
          && l.type === type
          && Math.abs(+l.strike - +strike) < 0.01
          && Math.abs((+l.dte || 0) - (+dte || 0)) < 1,
      );
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], qty: (+next[idx].qty || 1) + 1 };
        return next;
      }
      return [...prev, { action, type, strike: +strike, dte: +dte, qty: 1 }];
    });
    toast.success(`${action} ${type.toUpperCase()} ${strike} (${dte}d)`);
  }, []);

  // Drag-handler: a Strike Ribbon pill was dragged to a new strike. The
  // StrikeRibbon already snaps the value to the nearest listed strike before
  // calling this — we just commit the update.
  const updateLegStrike = useCallback((idx, newStrike) => {
    setLegs((prev) => {
      if (!Number.isFinite(newStrike)) return prev;
      if (idx < 0 || idx >= prev.length) return prev;
      const old = prev[idx];
      if (Math.abs(+old.strike - +newStrike) < 0.001) return prev;
      const next = [...prev];
      next[idx] = { ...old, strike: +newStrike };
      return next;
    });
  }, []);

  // Flatten the option chain's strikes_by_expiry into a unique sorted list
  // of listed strikes — used to snap drag-targets to real tradable values.
  const listedStrikes = useMemo(() => {
    const by = chain?.strikes_by_expiry || {};
    const set = new Set();
    Object.values(by).forEach((arr) => (arr || []).forEach((s) => set.add(+s)));
    const arr = Array.from(set).filter(Number.isFinite).sort((a, b) => a - b);
    return arr.length ? arr : null;
  }, [chain]);

  const removeLeg = useCallback((idx) => {
    setLegs((prev) => prev.filter((_, i) => i !== idx));
    toast("Leg removed");
  }, []);

  const clearLegs = useCallback(() => {
    setLegs([]);
    setStrategy(null);
    toast("Cleared all legs");
  }, []);

  const legsForStress = useMemo(
    () => legs.map(({ qty, ...rest }) => ({ qty: qty || 1, ...rest })),
    [legs],
  );

  const strategyName = useMemo(() => classifyStrategy(legs).name, [legs]);

  const expirations = chain?.expirations || [];
  const selectedExpiries = useMemo(
    () => Array.from(new Set(legs.map((l) => {
      const exp = expirations.find((e) => Math.abs(e.dte - l.dte) < 1);
      return exp?.date;
    }).filter(Boolean))),
    [legs, expirations],
  );

  // Strategy picker preset: apply a template via the existing builder. The
  // picker is now a "Strategy Library" shortcut, NOT the default surface.
  const onPickTemplate = useCallback(async (tpl) => {
    setShowPicker(false);
    if (!spot) { toast.error("Waiting on price"); return; }
    setBuildLoading(true);
    try {
      const built = await buildForgeStrategy({
        template_id: tpl.id,
        symbol: ticker,
        spot,
        dte: tpl.default_dte || 30,
        iv: iv30,
        width_pct: tpl.default_width_pct || 4,
        strike_pct: tpl.default_strike_pct || 0,
        qty: 1,
      });
      if (built?.metrics?.legs?.length) {
        setLegs(built.metrics.legs.map((l) => ({
          action: l.action,
          type: l.type,
          strike: Number(l.strike),
          dte: Number(l.dte),
          qty: Number(l.qty) || 1,
        })));
      }
      toast.success(`Loaded ${tpl.name}`);
    } catch (e) {
      toast.error("Failed to load template");
    } finally {
      setBuildLoading(false);
    }
  }, [ticker, spot, iv30]);

  return (
    <div className="min-h-screen bg-[#0B1426] text-white" data-testid="forge-page">
      <Header />
      <div className="h-header-with-safe" />
      <Toaster theme="dark" position="bottom-right" />

      <ForgeHeaderBar
        strategyName={strategyName}
        ticker={ticker}
        spot={spot}
        changePct={changePct}
        expirations={Array.from(new Set(legs.map((l) => Math.round(l.dte)).filter((n) => n > 0))).sort((a, b) => a - b)}
        legCount={legs.length}
        netPremium={strategy?.metrics?.net_premium}
        onOpenPicker={() => setShowPicker(true)}
        onOpenRecommend={() => setShowRecommend(true)}
        onOpenOptimizer={() => setShowOptimizer(true)}
        onOpenSaved={() => setShowSaved(true)}
        onOpenShare={() => setShowShare(true)}
        canShare={!!legs.length}
      />

      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-4 space-y-3">
        {/* Ticker picker + utility */}
        <div className="flex flex-wrap items-center gap-3 bg-[#0E1A30] border border-white/10 rounded-xl px-3 py-2">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-white/45">Underlying</span>
            <Select value={ticker} onValueChange={setTicker}>
              <SelectTrigger
                data-testid="forge-ticker-select"
                className="h-8 w-[160px] bg-[#050912] border-white/10 text-[12px] num"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0B1426] border-white/10 text-white">
                <SelectItem value="SPY">SPY · S&P 500</SelectItem>
                <SelectItem value="QQQ">QQQ · Nasdaq 100</SelectItem>
                <SelectItem value="IWM">IWM · Russell 2000</SelectItem>
                <SelectItem value="DIA">DIA · Dow</SelectItem>
                <SelectItem value="AAPL">AAPL</SelectItem>
                <SelectItem value="MSFT">MSFT</SelectItem>
                <SelectItem value="NVDA">NVDA</SelectItem>
                <SelectItem value="TSLA">TSLA</SelectItem>
                <SelectItem value="AMZN">AMZN</SelectItem>
                <SelectItem value="META">META</SelectItem>
                <SelectItem value="GOOGL">GOOGL</SelectItem>
                {(pickerData.indices || []).map((it) => (
                  <SelectItem key={it.symbol} value={it.symbol}>{it.symbol} · {it.label}</SelectItem>
                ))}
                {(pickerData.futures || []).map((it) => (
                  <SelectItem key={it.symbol} value={it.symbol}>{it.symbol} · {it.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <button
            type="button"
            onClick={() => setShowPicker(true)}
            data-testid="forge-open-library"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            <BookOpen className="w-3.5 h-3.5" /> Strategy Library
          </button>

          {legs.length > 0 && (
            <button
              type="button"
              onClick={clearLegs}
              data-testid="forge-clear-legs"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-[#EF4444]/30 text-[#EF4444] hover:bg-[#EF4444]/10 transition ml-auto"
            >
              <Trash2 className="w-3.5 h-3.5" /> Clear ({legs.length})
            </button>
          )}
        </div>

        {/* STRATEGY OF THE DAY — macro-aware top pick for the current ticker */}
        <StrategyOfDayCard
          ticker={ticker}
          onApply={(rec) => onPickTemplate({
            id: rec.template_id,
            name: rec.template_name,
            default_dte: rec.default_dte,
            default_width_pct: rec.default_width_pct,
            default_strike_pct: rec.default_strike_pct,
          })}
        />

        {/* EXPIRATION STRIP */}
        <ExpirationStrip
          expirations={expirations}
          selected={selectedExpiries.length ? selectedExpiries : (activeExpiry ? [activeExpiry] : [])}
          legDates={selectedExpiries}
          onPick={setActiveExpiry}
        />

        {/* STRIKE RIBBON + OPTION CHAIN — single unified card */}
        <div className="bg-[#0E1A30] border border-white/10 rounded-xl overflow-hidden">
          <StrikeRibbon
            legs={legs}
            spot={spot}
            onRemoveLeg={removeLeg}
            onLegStrikeChange={updateLegStrike}
            listedStrikes={listedStrikes}
            bare
          />
          <div className="h-px bg-white/10" />
          <OptionChainTable
            chain={chain}
            expiry={activeExpiry}
            spot={spot}
            legs={legs}
            onAddLeg={addLegFromChain}
            onClearLegs={clearLegs}
            bare
          />
        </div>

        {/* METRICS STRIP */}
        <MetricsStrip
          netPremium={strategy?.metrics?.net_premium}
          maxProfit={strategy?.metrics?.max_profit}
          maxLoss={strategy?.metrics?.max_loss}
          pop={strategy?.metrics?.pop}
          breakevens={strategy?.metrics?.breakevens || []}
          unrealized={null}
        />

        {/* VIEW TABS */}
        <ViewTabs active={viewMode} onChange={setViewMode} />

        {/* PAYOFF VIEW — graph (default) | dollars/percent variants | table | contract */}
        {viewMode === "table" ? (
          <PayoffTable
            curve={strategy?.payoff_curve || []}
            spot={strategy?.metrics?.spot ?? spot}
            breakevens={strategy?.metrics?.breakevens || []}
            netPremium={strategy?.metrics?.net_premium}
            maxLoss={strategy?.metrics?.max_loss}
            mode="dollars"
          />
        ) : viewMode === "contract" ? (
          <ContractValueTable
            legs={strategy?.metrics?.legs || legs}
            pricing={strategy?.pricing}
            greeks={strategy?.metrics?.greeks}
          />
        ) : (
          <PayoffChart
            curve={strategy?.payoff_curve}
            todayCurve={strategy?.metrics?.today_curve}
            tnCurves={strategy?.metrics?.tn_curves}
            cone={strategy?.metrics?.cone}
            spot={strategy?.metrics?.spot ?? spot}
            breakevens={strategy?.metrics?.breakevens || []}
            maxProfit={strategy?.metrics?.max_profit}
            maxLoss={strategy?.metrics?.max_loss}
            pop={strategy?.metrics?.pop}
            vega={strategy?.metrics?.greeks?.vega}
            netPremium={strategy?.metrics?.net_premium}
            volumeOverlay={strategy?.volume_overlay}
            interactive={false}
            legs={legs}
            listedStrikes={null}
            externalTimeFrac={scnTimeFrac}
            externalShockPct={scnShockPct}
            externalIvShockVp={scnIvShockVp}
            setExternalTimeFrac={setScnTimeFrac}
            setExternalShockPct={setScnShockPct}
            setExternalIvShockVp={setScnIvShockVp}
            hideInlineScenario
            externalPnlMode={viewMode === "pnl_p" ? "percent" : viewMode === "pnl_d" ? "dollars" : null}
          />
        )}

        {/* SCENARIO FOOTER BAR — DATE / RANGE / IV in one horizontal strip */}
        <ChartScenarioBar
          timeFrac={scnTimeFrac} setTimeFrac={setScnTimeFrac}
          shockPct={scnShockPct} setShockPct={setScnShockPct}
          ivShockVp={scnIvShockVp} setIvShockVp={setScnIvShockVp}
          vega={strategy?.metrics?.greeks?.vega}
          expiryDays={
            Math.max(0, ...(legs || []).map((l) => Number(l.dte) || 0))
          }
          spot={spot}
          netPremium={strategy?.metrics?.net_premium}
        />

        {/* MATRIX HEATMAP — Date × Stock Price grid of P&L */}
        {strategy?.metrics?.tn_curves?.length > 0 && (
          <MatrixHeatmap
            tnCurves={strategy.metrics.tn_curves}
            spot={spot}
            onCellClick={({ frac, shockPct }) => {
              if (Number.isFinite(frac)) setScnTimeFrac(frac);
              if (Number.isFinite(shockPct)) setScnShockPct(shockPct);
            }}
          />
        )}

        {/* Greeks summary */}
        <GreeksTable
          greeks={strategy?.metrics?.greeks}
          netPremium={strategy?.metrics?.net_premium}
          breakevens={strategy?.metrics?.breakevens}
          pop={strategy?.metrics?.pop}
          maxProfit={strategy?.metrics?.max_profit}
          maxLoss={strategy?.metrics?.max_loss}
          legs={strategy?.metrics?.legs}
          pricing={strategy?.pricing}
          earnings={strategy?.earnings}
        />

        {/* Advanced tools — two pill buttons that open popup modals + the
            existing Optimizer / Recommend actions (also in the header). */}
        <div
          className="bg-[#0E1A30] border border-white/10 rounded-xl p-3 flex flex-wrap items-center gap-2"
          data-testid="forge-advanced-toolbar"
        >
          <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">
            Advanced
          </span>
          <button
            type="button"
            onClick={() => setShowStressMargin(true)}
            data-testid="forge-open-stress-margin"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            Stress test &amp; margin forecast
          </button>
          <button
            type="button"
            onClick={() => setShowLogAlpha(true)}
            data-testid="forge-open-log-alpha"
            className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-white/15 text-white/80 hover:text-white hover:border-white/35 transition"
          >
            Strategy log &amp; alpha insights
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setShowOptimizer(true)}
              data-testid="forge-toolbar-optimizer"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md bg-[#3B82F6] hover:bg-[#2563EB] text-white font-medium transition"
            >
              Optimize Strategy
            </button>
            <button
              type="button"
              onClick={() => setShowRecommend(true)}
              data-testid="forge-toolbar-recommend"
              className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] px-3 py-1.5 rounded-md border border-[#A98CFF]/40 text-[#C4B5FD] hover:text-white hover:border-[#A98CFF] transition"
            >
              Recommend Best Strategy
            </button>
          </div>
        </div>

        <p className="text-[10.5px] text-white/35 inline-flex items-center gap-1.5">
          <HelpCircle className="w-3 h-3" />
          Click an <b className="text-[#22C55E]">Ask</b> in the chain to BUY,
          a <b className="text-[#EF4444]">Bid</b> to SELL — strategy auto-names
          as soon as your legs match a known shape.
        </p>
      </main>

      <StrategyPickerModal
        open={showPicker}
        onClose={() => setShowPicker(false)}
        templates={templates}
        activeId={null}
        onPick={onPickTemplate}
      />

      {showOptimizer && (
        <OptimizerModal
          symbol={ticker}
          spot={spot}
          iv={iv30}
          onClose={() => setShowOptimizer(false)}
          onPick={() => setShowOptimizer(false)}
        />
      )}

      <SavedStrategiesDrawer
        open={showSaved}
        onClose={() => setShowSaved(false)}
        strategy={strategy}
        onLoad={(s) => {
          if (s.legs?.length) {
            setLegs(s.legs.map((l) => ({
              action: l.action, type: l.type, strike: +l.strike,
              dte: +l.dte, qty: +l.qty || 1,
            })));
            setShowSaved(false);
            toast.success(`Loaded: ${s.name}`);
          }
        }}
      />

      {showRecommend && (
        <RecommendModal
          defaultSymbol={ticker}
          onClose={() => setShowRecommend(false)}
          onPick={() => setShowRecommend(false)}
        />
      )}

      {showShare && (
        <ShareModal
          strategy={strategy}
          ticker={ticker}
          onClose={() => setShowShare(false)}
        />
      )}

      {/* Bottom popup modals — replaces the old accordion. */}
      <ForgeModal
        open={showStressMargin}
        title="Stress Test & Margin Forecast"
        subtitle={`${ticker} · ${legs.length} leg${legs.length === 1 ? "" : "s"}`}
        onClose={() => setShowStressMargin(false)}
        testid="forge-modal-stress-margin"
        maxWidth="max-w-6xl"
      >
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-5">
          <StressTestPanel
            legs={legsForStress}
            spot={spot}
            iv={iv30}
            r={0.045}
          />
          <MarginForecastPanel
            legs={legsForStress}
            spot={spot}
            iv={iv30}
            r={0.045}
            isIndex={
              ticker.startsWith("/") ||
              ["SPX", "XSP", "NDX", "RUT", "VIX", "DJX"].includes(ticker)
            }
          />
        </div>
      </ForgeModal>

      <ForgeModal
        open={showLogAlpha}
        title="Strategy Log & Alpha Insights"
        subtitle="Track positions and review aggregate edge"
        onClose={() => setShowLogAlpha(false)}
        testid="forge-modal-log-alpha"
        maxWidth="max-w-6xl"
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <StrategyLog
            currentStrategy={strategy}
            ticker={ticker}
            inputs={{ spot, iv: iv30, dte: 30 }}
            onLogged={() => {}}
          />
          <AlphaInsights refreshKey={0} />
        </div>
      </ForgeModal>
    </div>
  );
}
